import { useMemo, useState } from "react";

import {
  ESTADOS_DEVOLUCION, cuando, devolverTodo, itemsDeDevolucion, resumenDevolucion,
} from "../../services/herramientas";

/* «DEVOLVIÓ TODO» (tickets 328 y 329, Nico 06/10): «Cuando un chofer es
 * despedido o renuncia tengo que marcar que me devolvió las cosas. Esas cosas
 * se vuelven a poner en stock si están en condiciones y queda la fecha de
 * devolución y a quién se devolvió guardado».
 *
 * Se lista todo lo que tiene y de cada cosa se dice cómo volvió. Arranca todo
 * en «volvió bien»: es el caso normal, y lo que hay que marcar es la
 * excepción. Lo que se elige acá es lo que después le llega a Personal para la
 * liquidación final, así que ANTES de guardar se dice en una línea qué se va
 * a anotar.
 *
 * Quién lo recibe y cuándo no se preguntan: los pone la base con la sesión. */

/* Lo que devolvió, cosa por cosa (ticket 477, Nico 22/10): «quiero poder ver
 * la caja devuelta, quién tenía qué». La fila guarda la lista CONGELADA en
 * `items` ({nombre, codigo, cantidad, estado}); acá se muestra tal cual, sin
 * volver a la base. Una fila vieja o rota sin `items` no rompe: no se lista. */
function QueDevolvio({ items }) {
  const lista = Array.isArray(items) ? items : [];
  if (lista.length === 0) return null;
  return (
    <details className="hrr-que-devolvio">
      <summary>Qué devolvió ({lista.length})</summary>
      <ul className="hrr-devolucion-lista">
        {lista.map((x, i) => {
          // Sin estado es «devuelta»: es lo que asume la base al guardarla.
          const estado = x?.estado || "devuelta";
          return (
            <li key={`${x?.prestamo_id || x?.entrega_id || "x"}-${i}`} data-devuelto={estado}>
              <span>
                {Number(x?.cantidad) > 1 ? `${x.cantidad} × ` : ""}<b>{x?.nombre || "(sin nombre)"}</b>
                {x?.codigo ? <small className="tenue"> {x.codigo}</small> : null}
              </span>
              {estado === "devuelta"
                ? <small className="tenue">{ESTADOS_DEVOLUCION.devuelta}</small>
                : <strong>{ESTADOS_DEVOLUCION[estado] || estado}</strong>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/* Quién recibió y qué no volvió bien, para la línea de cada devolución. */
function lineaDevolucion(devolucion) {
  const lista = Array.isArray(devolucion.items) ? devolucion.items : [];
  const faltan = lista.filter((x) => (x?.estado || "devuelta") !== "devuelta");
  return (devolucion.recibio_nombre ? ` · recibió ${devolucion.recibio_nombre}` : "")
    + (faltan.length > 0
      ? ` · no volvieron bien: ${faltan.map((x) => x?.nombre || "(sin nombre)").join(", ")}`
      : " · todo en condiciones");
}

/* La última devolución arriba y a la vista; las anteriores (si devolvió más
 * de una vez: volvió a entrar y se volvió a ir), plegadas abajo. */
export function UltimaDevolucion({ devolucion, anteriores = [] }) {
  if (!devolucion) return null;
  const viejas = (anteriores || []).filter(Boolean);
  return (
    <div className="hrr-ultima-devolucion" data-ultima-devolucion={devolucion.id}>
      <p className="tenue">
        Devolvió todo el {cuando(devolucion.devuelto_en)}{lineaDevolucion(devolucion)}
      </p>
      <QueDevolvio items={devolucion.items} />
      {viejas.length > 0 && (
        <details className="hrr-devoluciones-anteriores">
          <summary>Devoluciones anteriores ({viejas.length})</summary>
          {viejas.map((d) => (
            <div key={d.id} className="hrr-devolucion-anterior" data-devolucion={d.id}>
              <p className="tenue">El {cuando(d.devuelto_en)}{lineaDevolucion(d)}</p>
              <QueDevolvio items={d.items} />
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

export default function DevolvioTodo({ dni, nombre, prestamos, entregas, onHecho }) {
  const base = useMemo(() => itemsDeDevolucion(prestamos, entregas), [prestamos, entregas]);
  const [abierto, setAbierto] = useState(false);
  const [estados, setEstados] = useState({});
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const items = base.map((x) => ({ ...x, estado: estados[x.clave] || "devuelta" }));
  const res = resumenDevolucion(items);

  if (base.length === 0) return null;

  if (!abierto) {
    return (
      <p className="hrr-devolvio-todo">
        <button type="button" className="hrr-btn hrr-btn--chico"
                onClick={() => { setAbierto(true); setError(""); }}>
          📦 Devolvió todo
        </button>
      </p>
    );
  }

  const guardar = async () => {
    setGuardando(true);
    setError("");
    const r = await devolverTodo({ dni, items, observaciones });
    setGuardando(false);
    if (!r.ok) { setError(r.error || "No se pudo consultar."); return; }
    setAbierto(false);
    setEstados({});
    setObservaciones("");
    onHecho?.(r.id);
  };

  return (
    <section className="hrr-aviso hrr-devolvio-todo-panel" aria-label="Devolvió todo">
      <p><b>{nombre} devuelve todo</b></p>
      <ul className="hrr-devolucion-lista">
        {items.map((x) => (
          <li key={x.clave} data-devolucion-item={x.clave}>
            <span>
              {x.cantidad > 1 ? `${x.cantidad} × ` : ""}<b>{x.nombre}</b>
              {x.codigo ? <small className="tenue"> {x.codigo}</small> : null}
            </span>
            <select aria-label={`Cómo volvió ${x.nombre}`} value={x.estado}
                    disabled={guardando}
                    onChange={(e) => setEstados({ ...estados, [x.clave]: e.target.value })}>
              {Object.entries(ESTADOS_DEVOLUCION).map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </li>
        ))}
      </ul>
      <input type="text" aria-label="Observaciones de la devolución" placeholder="Observaciones"
             value={observaciones} disabled={guardando}
             onChange={(e) => setObservaciones(e.target.value)} />
      <p className="hrr-devolucion-resumen" role="status">
        {res.faltantes === 0
          ? `Vuelven las ${res.total} en condiciones.`
          : `De ${res.total}: ${res.devuelta} en condiciones`
            + (res.rota ? `, ${res.rota} rota${res.rota === 1 ? "" : "s"}` : "")
            + (res.perdida ? `, ${res.perdida} que no volvi${res.perdida === 1 ? "ó" : "eron"}` : "")
            + "."}
      </p>
      {error && <p className="aduana-error" role="alert">{error}</p>}
      <button type="button" className="hrr-btn hrr-btn--principal" disabled={guardando}
              onClick={guardar}>
        {guardando ? "Anotando…" : "Anotar la devolución"}
      </button>{" "}
      <button type="button" className="hrr-btn" disabled={guardando}
              onClick={() => { setAbierto(false); setError(""); }}>
        Cancelar
      </button>
    </section>
  );
}
